/**
 * Pins (1.I-1 deliverable 22): the pinned entries and groups, the pin actions shared with the
 * tree, Recent and the header, and drag reordering within one level (`@dnd-kit` sortable with
 * its keyboard sensor). Titles come from `docs.titles`; a pin's own label wins.
 */

import { flagsFromMe, sidebarAdd, sidebarRemove, sidebarUpdate } from '@codex/contracts';
import { Button, Icon, IconButton, Skeleton } from '@codex/ui';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { api } from '../auth/client.js';
import { docHref } from '../editor-services/adapter.js';
import { invalidatePinQueries, useSavedViews } from '../views/data/index.js';
import { viewHref } from '../views/href.js';
import { useNotify } from './notices.js';
import { type OpenEvent, useGoTo, useOpenDoc } from './open-doc.js';
import {
  afterIdForDrop,
  buildPinRows,
  canUnpin,
  hasViewPin,
  PINS_EMPTY,
  type PinRow,
  pinsByDocument,
  pinTitleIds,
  reorderItems,
  type ViewPinInfo,
} from './pins-model.js';
import { sidebarKey, useSidebarItems, useTitles } from './queries.js';
import { useShellSession } from './session.js';

export const PIN_FAILED = "The page couldn't be pinned.";
export const UNPIN_FAILED = "The pin couldn't be removed.";
export const MOVE_FAILED = "The pin couldn't be moved.";

export interface PinActions {
  /** Document id to the pin that holds it. */
  readonly pinned: ReadonlyMap<string, string>;
  readonly pin: (docId: string) => Promise<void>;
  readonly unpin: (itemId: string) => Promise<void>;
}

export function usePinActions(): PinActions {
  const client = useQueryClient();
  const notify = useNotify();
  const items = useSidebarItems();
  const pinned = useMemo(() => pinsByDocument(items.data ?? []), [items.data]);
  const refresh = useCallback(() => invalidatePinQueries(client), [client]);
  const pin = useCallback(
    async (docId: string) => {
      try {
        await api(sidebarAdd, { body: { kind: 'document', refId: docId } });
      } catch {
        notify(PIN_FAILED);
      }
      await refresh();
    },
    [notify, refresh],
  );
  const unpin = useCallback(
    async (itemId: string) => {
      try {
        await api(sidebarRemove, { params: { id: itemId } });
      } catch {
        notify(UNPIN_FAILED);
      }
      await refresh();
    },
    [notify, refresh],
  );
  return { pinned, pin, unpin };
}

/** The "Pin" button of a tree or Recent row; pressed when the page is pinned already. */
export function PinDocButton({
  docId,
  actions,
  focusable = true,
}: {
  docId: string;
  actions: PinActions;
  focusable?: boolean;
}) {
  return (
    <IconButton
      icon="pin"
      label="Pin"
      pressed={actions.pinned.has(docId)}
      onClick={() => void actions.pin(docId)}
      className="cx-row-action"
      {...(focusable ? {} : { tabIndex: -1 })}
    />
  );
}

/**
 * A live view pin's link (2.K s2): the view's layout glyph, then its label, on one line. The
 * glyph is inline content of the label (so a long label is cut with an ellipsis after it); the
 * app's Tailwind preflight makes every `svg` a block, so `cx-pin__glyph` puts it back inline
 * (exit-2a fix round 2, DECISIONS 592).
 */
export function ViewPinLink({
  viewId,
  layout,
  text,
  onOpen,
}: {
  viewId: string;
  layout: string | null;
  text: string;
  onOpen: (event: OpenEvent) => void;
}) {
  return (
    <a
      className="cx-nav-row__label cx-shell-link"
      href={viewHref(viewId)}
      title={text}
      data-view-id={viewId}
      onClick={onOpen}
    >
      <Icon name={layout === 'list' ? 'stack' : 'table'} size={14} className="cx-pin__glyph" />
      {text}
    </a>
  );
}

function PinRowView({ row, actions }: { row: PinRow; actions: PinActions }) {
  const open = useOpenDoc();
  const goTo = useGoTo();
  const goToView = (id: string, event: OpenEvent) => goTo(viewHref(id), event);
  const sortable = useSortable({ id: row.id });
  const [collapsed, setCollapsed] = useState(false);
  const style = {
    transform: sortable.transform
      ? `translate3d(${sortable.transform.x}px, ${sortable.transform.y}px, 0)`
      : undefined,
    transition: sortable.transition,
  };
  const link = row.openId !== null && (row.status === 'live' || row.status === 'merged');
  return (
    <li
      ref={sortable.setNodeRef}
      style={style}
      className="cx-pin"
      data-pin-id={row.id}
      data-status={row.status}
      data-dragging={sortable.isDragging ? '' : undefined}
    >
      <div className="cx-pin__row cx-nav-row">
        <button
          type="button"
          ref={sortable.setActivatorNodeRef}
          className="cx-pin__handle"
          {...sortable.attributes}
          {...sortable.listeners}
          aria-label={`Move ${row.text}`}
        >
          <Icon name="handle" />
        </button>
        {row.kind === 'group' && (
          <button
            type="button"
            className="cx-pin__group"
            aria-expanded={!collapsed}
            onClick={() => setCollapsed((value) => !value)}
          >
            <Icon name={collapsed ? 'caret-right' : 'caret-down'} />
            <span className="cx-nav-row__label" title={row.text}>
              {row.text}
            </span>
          </button>
        )}
        {row.kind !== 'group' &&
          (row.viewId ? (
            <ViewPinLink
              viewId={row.viewId}
              layout={row.layout ?? null}
              text={row.text}
              onOpen={(event) => goToView(row.viewId as string, event)}
            />
          ) : link ? (
            <a
              className="cx-nav-row__label cx-shell-link"
              href={docHref(row.openId as string)}
              title={row.text}
              onClick={(event) => open(row.openId as string, event)}
            >
              {row.text}
            </a>
          ) : (
            <span className="cx-nav-row__label cx-shell-static" title={row.text}>
              {row.text}
            </span>
          ))}
        {canUnpin(row) && (
          <IconButton
            icon="x"
            label="Unpin"
            onClick={() => void actions.unpin(row.id)}
            className="cx-row-action"
          />
        )}
      </div>
      {row.kind === 'group' && !collapsed && (
        <PinList rows={row.children} actions={actions} nested />
      )}
    </li>
  );
}

function PinList({
  rows,
  actions,
  nested = false,
}: {
  rows: readonly PinRow[];
  actions: PinActions;
  nested?: boolean;
}) {
  return (
    <SortableContext items={rows.map((row) => row.id)} strategy={verticalListSortingStrategy}>
      <ul
        className="cx-nav-section__list cx-pin-list"
        data-testid={nested ? undefined : 'shell.sidebar.pins'}
      >
        {rows.map((row) => (
          <PinRowView key={row.id} row={row} actions={actions} />
        ))}
      </ul>
    </SortableContext>
  );
}

export function PinsSection() {
  const client = useQueryClient();
  const notify = useNotify();
  const items = useSidebarItems();
  const titles = useTitles(pinTitleIds(items.data ?? []));
  const actions = usePinActions();
  const me = useShellSession();
  const viewsOn = me ? flagsFromMe(me).catalogue : false;
  // The views list is read only for a live view pin with the catalogue on (2.K deliverable 13).
  const savedViews = useSavedViews(undefined, viewsOn && hasViewPin(items.data ?? []));
  const viewInfo = useMemo<ViewPinInfo | null | undefined>(() => {
    if (!hasViewPin(items.data ?? [])) return undefined;
    if (!viewsOn) return null;
    if (!savedViews.data) return undefined;
    return new Map(savedViews.data.map((v) => [v.id, { name: v.name, layout: v.layout }]));
  }, [items.data, viewsOn, savedViews.data]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const rows = useMemo(() => {
    const map = new Map((titles.data ?? []).map((title) => [title.id, title]));
    return buildPinRows(items.data ?? [], map, viewInfo);
  }, [items.data, titles.data, viewInfo]);

  const onDragEnd = useCallback(
    async ({ active, over }: DragEndEvent) => {
      const all = items.data ?? [];
      const dragged = all.find((item) => item.id === active.id);
      if (!over || !dragged) return;
      const siblings = all.filter((item) => item.parentId === dragged.parentId).map((i) => i.id);
      const after = afterIdForDrop(siblings, String(active.id), String(over.id));
      if (after === undefined) return;
      client.setQueryData(sidebarKey, reorderItems(all, dragged.id, after));
      try {
        await api(sidebarUpdate, { params: { id: dragged.id }, body: { afterId: after } });
      } catch {
        notify(MOVE_FAILED);
      }
      await client.invalidateQueries({ queryKey: sidebarKey });
    },
    [items.data, client, notify],
  );

  return (
    <section className="cx-nav-section" aria-label="Pinned">
      <div className="cx-nav-section__head">
        <h2 className="cx-nav-section__title">Pinned</h2>
      </div>
      {items.isPending ? (
        <Skeleton lines={2} variant="rows" label="Loading pins" />
      ) : items.isError ? (
        <div className="cx-shell-list-error">
          <p role="alert">Couldn't load this list.</p>
          <Button size="sm" onClick={() => void items.refetch()}>
            Try again
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <ul className="cx-nav-section__list" data-testid="shell.sidebar.pins">
          <li className="cx-shell-empty">{PINS_EMPTY}</li>
        </ul>
      ) : (
        <DndContext sensors={sensors} onDragEnd={(event) => void onDragEnd(event)}>
          <PinList rows={rows} actions={actions} />
        </DndContext>
      )}
    </section>
  );
}
